import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Play, RotateCcw, Square, Unlock, Zap } from 'lucide-react';
import type { Charger } from '../../api/chargers';
import { usePermission } from '../../auth/permissions';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { StatusLabel } from '../../components/StatusLabel';
import { formatAgo, formatDateTime } from '../../lib/format';
import { colours, radius } from '../../theme';
import { connectorsOf, kw, kwh, STATUS_DISPLAY } from './status';
import { StartSessionDialog } from './StartSessionDialog';
import { useCommand } from './useCommand';

const SECURITY = ['None (no password)', 'Password', 'Password over an encrypted connection (wss://)'];

/** Each socket's status and session, the quick actions, and the facts about the charger. */
export function OverviewTab({ charger: c }: { charger: Charger }) {
  const canCommand = usePermission('chargers.command');
  const canQuery = usePermission('chargers.query');
  const dialog = useDialog();
  const { run, busy } = useCommand(c);
  const accepted = c.registration === 'accepted';
  const live = c.isConnected && accepted && canCommand;
  const canAsk = c.isConnected && accepted && canQuery;

  const confirmRun = async (title: string, message: string, label: string, body: Parameters<typeof run>[0]) => {
    if (await dialog.confirm({ title, message, confirmLabel: label })) await run(body);
  };

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: c.connectorCount > 1 ? '1fr 1fr' : '1fr' } }}>
        {connectorsOf(c).map((k) => {
          const session = c.activeSessions.find((s) => s.connectorId === k.connectorId);
          const d = STATUS_DISPLAY[k.status];
          return (
            <Paper key={k.connectorId} variant="outlined" sx={{ p: 2.5, borderRadius: `${radius.card}px` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 1 }}>
                <Typography variant="h3" component="h3">
                  {c.connectorCount > 1 ? `Connector ${k.connectorId}` : 'Connector'}
                </Typography>
                <StatusLabel tone={d.tone}>{d.label}</StatusLabel>
              </Box>
              {k.errorCode && k.errorCode !== 'NoError' && (
                <Typography sx={{ color: colours.faultRed, mb: 1 }}>
                  {k.errorCode}
                  {k.info ? `: ${k.info}` : ''}
                </Typography>
              )}
              {session ? (
                <Box sx={{ mb: 2 }}>
                  <Typography>{session.driverName ?? (session.idTag?.startsWith('ADM-') ? 'Started from Pulsepoint' : session.idTag?.startsWith('PUB-') ? 'Public charging guest' : `Card ${session.idTag ?? ''}`)}</Typography>
                  <Typography color="textSecondary" className="num">
                    Since {formatDateTime(session.startedAt)} · {kwh(session.energyWh)}
                    {session.powerW ? ` · ${kw(session.powerW)} now` : ''}
                  </Typography>
                </Box>
              ) : (
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                  {k.at ? `${k.status === 'Offline' ? 'Last heard from' : 'Since'} ${formatAgo(k.at)}` : 'No session'}
                </Typography>
              )}
              {live && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {session ? (
                    <Button
                      size="small"
                      startIcon={<Square size={16} strokeWidth={1.75} />}
                      disabled={busy}
                      onClick={() => void confirmRun('Stop this session?', 'The charger stops charging and ends the session. The driver can unplug.', 'Stop the session', { action: 'RemoteStopTransaction', transactionId: session.ocppTransactionId })}
                    >
                      Stop the session
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<Play size={16} strokeWidth={1.75} />}
                      disabled={busy || k.status === 'Faulted' || k.status === 'Unavailable'}
                      onClick={() => void dialog.open(StartSessionDialog, { charger: c, connectorId: k.connectorId })}
                    >
                      Start a session
                    </Button>
                  )}
                  <Button size="small" startIcon={<Unlock size={16} strokeWidth={1.75} />} disabled={busy} onClick={() => void run({ action: 'UnlockConnector', connectorId: k.connectorId })}>
                    Unlock
                  </Button>
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>

      {canAsk && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {live && (
            <>
          <Button
            startIcon={<RotateCcw size={18} strokeWidth={1.75} />}
            disabled={busy}
            onClick={() => void confirmRun('Restart the charger?', 'A soft restart: it finishes any session cleanly, then restarts. It is back in about a minute.', 'Restart', { action: 'Reset', type: 'Soft' })}
          >
            Restart
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              void confirmRun('Hard restart?', 'The charger restarts at once, like switching it off and on. Any session ends without a final meter reading. Use it when a soft restart does nothing.', 'Hard restart', { action: 'Reset', type: 'Hard' })
            }
          >
            Hard restart
          </Button>
            </>
          )}
          <Button startIcon={<Zap size={18} strokeWidth={1.75} />} disabled={busy} onClick={() => void run({ action: 'TriggerMessage', requestedMessage: 'StatusNotification' })}>
            Ask for its status
          </Button>
        </Box>
      )}
      {!c.isConnected && (
        <Typography color="textSecondary">
          It is offline{c.lastSeenAt ? `, last heard from ${formatAgo(c.lastSeenAt)}` : ''}. Commands work once it reconnects. Check its power and internet connection.
        </Typography>
      )}

      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, rowGap: 1, columnGap: 3, '& dt': { color: 'text.secondary' }, '& dd': { m: 0 } }}>
        <dt>Charge point ID</dt>
        <dd className="num">{c.chargePointId}</dd>
        <dt>Make and model</dt>
        <dd>{[c.vendor, c.model].filter(Boolean).join(' ') || 'Not reported yet'}</dd>
        <dt>Firmware</dt>
        <dd>{c.firmwareVersion ?? '—'}</dd>
        {c.serialNumber && (
          <>
            <dt>Serial number</dt>
            <dd>{c.serialNumber}</dd>
          </>
        )}
        <dt>Connection</dt>
        <dd>{c.isConnected ? `Online${c.lastBootAt ? `, started ${formatAgo(c.lastBootAt)}` : ''}` : `Offline${c.lastSeenAt ? `, last heard from ${formatDateTime(c.lastSeenAt)}` : ''}`}</dd>
        <dt>Security</dt>
        <dd>{SECURITY[c.securityProfile]}</dd>
      </Box>
    </Box>
  );
}
